import {
  Checkbox,
  CheckboxProps,
  TextField,
  TextFieldProps,
  styled,
} from '@mui/material'
import { PropsWithChildren, useMemo } from 'react'
import checkboxActive from '../../../../assets/checkbox_active_m.svg'
import checkboxInactive from '../../../../assets/checkbox_inactive_m.svg'
import { Support } from './Support'
import { LoginFooter } from './LoginFooter'
import bannerImage from '../../../../assets/flagxs_logo_horizontal_color.svg'
import { colorPalette } from '../../../style/colorPalette'

export const LoginContainer = ({ children }: PropsWithChildren<{}>) => {
  return (
    <LoginRoot>
      <Logo src={bannerImage} height={31} />
      <LoginMain>
        <Support />
        <LoginMainDiv>{children}</LoginMainDiv>
      </LoginMain>
      <LoginFooter />
    </LoginRoot>
  )
}

const LoginRoot = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  backgroundColor: colorPalette.monotone[1],
  width: '100vw',
  justifyContent: 'flex-start',
})

const Logo = styled('img')({
  display: 'flex',
  flexDirection: 'row',
  top: '20px',
  alignItems: 'center',
  position: 'absolute',
  left: '20px',
})

const LoginMain = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100vw',
  justifyContent: 'space-around',
  height: 'calc(100% - 92px)',
})

const LoginMainDiv = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '48px 32px',
  gap: '16px',
  width: '540px',
  borderRadius: '12px',
  backgroundColor: colorPalette.monotone[0],
})

export const LoginTitle = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  color: colorPalette.monotone[10],
  fontSize: '20px',
  fontWeight: '600',
  paddingBottom: '20px',
})

export const ErrorMessage = styled('div')({
  display: 'flex',
  backgroundColor: colorPalette.pink[0],
  linearGradient: colorPalette.pink[5],
  border: `1px solid ${colorPalette.pink[5]}`,
  fontSize: '15px',
  fontWeight: '700',
  color: colorPalette.pink[5],
  width: '476px',
  padding: '12px 24px 12px 24px',
  borderRadius: '4px',
  justifyContent: 'center',
  alignItems: 'center',
  whiteSpace: 'pre-wrap',
  textAlign: 'center',
})

export const LoginInput = styled((props: TextFieldProps) => (
  <TextField variant="outlined" {...props} />
))({
  width: '360px',
  '& .MuiOutlinedInput-input': {
    height: '14px',
    padding: '20px 16px',
    color: colorPalette.monotone[9],
    caretColor: colorPalette.skyBlue[7],
    fontSize: '16px !important',
  },
  '& .MuiOutlinedInput-root': {
    height: '54px',
    '&:focus-within': {
      backgroundColor: colorPalette.blue[0],
    },
    '& fieldset': {
      borderColor: colorPalette.monotone[2],
    },
    '&:hover fieldset': {
      borderColor: colorPalette.skyBlue[7],
    },
    '&.Mui-focused fieldset': {
      borderColor: colorPalette.skyBlue[7],
      borderWidth: '1px',
    },
  },
})

export const LoginPassword = styled(LoginInput)({
  width: '444px',
  '& .MuiFormHelperText-root': {
    margin: '2px 14px 0',
    lineHeight: 1.2,
  },
})

type LoginButtonProps = {
  color: 'skyBlue' | 'monotone'
  disabled?: boolean
}

export const LoginButton = styled('button')(({
  color,
  disabled,
}: LoginButtonProps) => {
  const customizedStyle = useMemo(() => {
    if (disabled) {
      return {
        border: 'none',
        backgroundColor: colorPalette.monotone[2],
        color: colorPalette.monotone[4],
      }
    }
    switch (color) {
      case 'skyBlue':
        return {
          border: 'none',
          backgroundColor: colorPalette.skyBlue[7],
          color: colorPalette.monotone[0],
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: colorPalette.skyBlue[5],
          },
        }
      case 'monotone':
        return {
          border: `1px solid ${colorPalette.monotone[2]}`,
          backgroundColor: colorPalette.monotone[0],
          color: colorPalette.monotone[4],
          cursor: 'pointer',
          '&:hover': {
            border: `1px solid ${colorPalette.monotone[4]}`,
          },
        }
      default:
        return {}
    }
  }, [color, disabled])
  return {
    width: '300px',
    height: '30px',
    fontWeight: '600',
    borderRadius: '4px',
    gap: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...customizedStyle,
  }
})

export const LoginCheckbox = (props: CheckboxProps) => (
  <Checkbox
    {...props}
    icon={<img src={checkboxInactive} />}
    checkedIcon={<img src={checkboxActive} />}
  />
)

export const LoginLink = styled('a')({
  color: colorPalette.monotone[4],
  textDecoration: 'none',
  cursor: 'pointer',
})
